// Content for all data-driven service detail pages.
// Each page renders via src/pages/ServiceDetail.jsx

export const SERVICE_PAGES = {
  "nri-property-management-hyderabad": {
    seoTitle: "NRI Property Management Hyderabad | End-to-End Property Care",
    seoDescription:
      "Complete NRI property management in Hyderabad — tenant management, rent collection, inspections, maintenance, cleaning and documentation, handled locally on your behalf.",
    eyebrow: "NRI Property Management · Hyderabad",
    titleLines: ["Complete Property Management", "for NRI Owners"],
    intro:
      "You may live thousands of kilometres away. Your property doesn't have to. NRI Property Services is your professional local presence in Hyderabad — your eyes, ears and hands on the ground for everything your property needs.",
    blocks: [
      {
        heading: "Built for owners who can't always be there",
        copy: "Owning a property in Hyderabad is easy. Managing it from the USA, UK, Canada, Australia, Dubai, Singapore or Europe is not. Tenants, rent, maintenance, repairs, cleaning, inspections and documentation quickly become a full-time responsibility. We bridge that distance with a structured, accountable, transparent service.",
        items: [],
      },
      {
        heading: "What we manage for you",
        copy: "One point of contact for the complete life of your property:",
        items: [
          "Tenant search, shortlisting and verification assistance",
          "Rental agreement and lease renewal coordination",
          "Rent tracking, collection assistance and owner reporting",
          "Move-in and move-out coordination",
          "Periodic property inspections with photo/video evidence",
          "Maintenance, repairs, painting, cleaning and pest control",
          "Property make-ready and re-rental after vacancy",
          "Documentation, utility and society/RWA coordination",
        ],
      },
      {
        heading: "How we keep you in control",
        copy: "No major work begins without your approval. Every inspection and every job is documented with photographs, videos where applicable, invoices and completion updates. We don't just say it's done — we show you.",
        items: [],
      },
    ],
    ctaLabel: "Get a Free Property Assessment",
  },

  "rental-management": {
    seoTitle: "NRI Rental Property Management Hyderabad | Tenant & Rent Management",
    seoDescription:
      "End-to-end rental management for NRI owners in Hyderabad — tenant search, verification, agreements, rent collection, move-ins, move-outs and owner reporting.",
    eyebrow: "Rental & Tenant Management",
    titleLines: ["Rental Management,", "Handled End to End"],
    intro:
      "From finding the right tenant to the final inspection after they vacate — we manage the complete rental lifecycle of your Hyderabad property while you stay informed from anywhere in the world.",
    blocks: [
      {
        heading: "Everything your rental property needs",
        copy: "A single, accountable point of contact for the entire tenancy:",
        items: [
          "Tenant search and shortlisting",
          "Tenant coordination and communication",
          "Tenant background verification assistance",
          "Employment/reference verification where applicable",
          "Police/tenant verification assistance",
          "Rental agreement coordination",
          "Lease renewal coordination",
          "Move-in and move-out coordination",
          "Tenant handover",
          "Security deposit coordination",
          "Property condition documentation",
          "Tenant issue coordination",
          "Notice-period coordination",
          "Final inspection after vacating",
        ],
      },
      {
        heading: "Owner-first, not tenant-first",
        copy: "A traditional broker's job ends when the tenant signs. Ours begins there. We represent your interest for the life of the tenancy — renewals, issues, inspections and eventually a smooth handover to the next tenant.",
        items: [],
      },
    ],
    ctaLabel: "Find / Manage My Tenant",
  },

  "tenant-management": {
    seoTitle: "NRI Tenant Management Hyderabad | Tenant Coordination & Support",
    seoDescription:
      "Professional tenant management for NRI property owners in Hyderabad — tenant communication, issue coordination, renewals, notice periods and verification assistance.",
    eyebrow: "Tenant Management",
    titleLines: ["Your Tenant,", "Professionally Managed"],
    intro:
      "A good tenancy runs on communication and follow-through. We handle the day-to-day relationship with your tenant so small issues never become your late-night phone calls.",
    blocks: [
      {
        heading: "Ongoing tenant relationship management",
        copy: "Once a tenant is in, we stay involved:",
        items: [
          "Tenant coordination and day-to-day communication",
          "Tenant issue coordination — complaints to closure",
          "Rent due-date tracking and reminders",
          "Lease renewal coordination",
          "Notice-period coordination",
          "Move-in and move-out coordination",
          "Property condition documentation at every handover",
          "Tenant verification and background-check assistance",
          "Police verification assistance",
          "Security deposit coordination",
        ],
      },
      {
        heading: "Every interaction documented",
        copy: "Complaints, follow-ups, resolutions and handovers are recorded and shared with you. When a tenant reports a leakage, you see the photos, the quotation, the completed work and the invoice.",
        items: [],
      },
    ],
    ctaLabel: "Manage My Tenant",
  },

  "tenant-placement": {
    seoTitle: "Tenant Placement Services Hyderabad | NRI Tenant Finding & Screening",
    seoDescription:
      "Tenant placement for NRI-owned properties in Hyderabad — property photos, listing preparation, enquiry handling, shortlisting, verification assistance and move-in coordination.",
    eyebrow: "Tenant Placement",
    titleLines: ["The Right Tenant.", "Placed With Care."],
    intro:
      "A vacant property costs you money every month — but the wrong tenant costs far more. We manage the placement process end to end so your property earns sooner, with a tenant you can trust.",
    blocks: [
      {
        heading: "From vacant to tenanted",
        copy: "Our placement process:",
        items: [
          "Pre-rental property inspection",
          "Property photographs and listing preparation",
          "Tenant enquiry handling and shortlisting",
          "Tenant background verification assistance",
          "Employment/reference verification where applicable",
          "Police/tenant verification assistance",
          "Rental agreement coordination",
          "Security deposit coordination",
          "Move-in coordination and condition documentation",
        ],
      },
      {
        heading: "Screened, not just found",
        copy: "Anyone can find a tenant. We help you find the right one — verified where applicable, documented at handover, and set up for a well-managed tenancy from day one.",
        items: [],
      },
    ],
    ctaLabel: "Find a Tenant",
  },

  "rent-collection": {
    seoTitle: "Rent Collection Services for NRI Property Owners in Hyderabad",
    seoDescription:
      "Rent collection assistance for NRI landlords in Hyderabad — due-date tracking, tenant reminders, payment records, monthly reporting and transparent service charges.",
    eyebrow: "Rent Collection & Owner Remittance",
    titleLines: ["Rent Collected. Records Maintained.", "Owner Updated."],
    intro:
      "Rent should arrive like clockwork — and you should never have to chase it from another time zone. We track, remind, record and report, so you always know where your rent stands.",
    process: {
      heading: "How rent collection works",
      steps: [
        { title: "Due date recorded", copy: "Your tenant's rent due date is recorded in our system." },
        { title: "Payment tracked", copy: "We track the payment as the date approaches." },
        { title: "Tenant reminded", copy: "The tenant is reminded when required — politely and professionally." },
        { title: "Receipt maintained", copy: "Rent receipt/payment record is maintained for every month." },
        { title: "Owner informed", copy: "You are informed as soon as rent is received." },
        { title: "Charges shown", copy: "Applicable service charges are transparently shown." },
        { title: "Funds transferred", copy: "Funds are transferred through the appropriate banking channel based on your instructions and applicable regulations." },
        { title: "Monthly records", copy: "Monthly records are maintained for you — clean, complete, retrievable." },
      ],
    },
    blocks: [
      {
        heading: "Collection assistance vs. international remittance",
        copy: "These are two different things. We assist with collecting rent locally and maintaining records. International remittance of funds is governed by banking, FEMA and tax requirements that depend on your residency status, account type (NRO/NRE), property type and transaction.",
        items: [],
      },
    ],
    note: "Tax, TDS, FEMA, repatriation and international remittance requirements can vary depending on the owner's residency status, account type, property type and transaction. We can coordinate with your CA/bank or appropriate professional advisor where required. This website does not provide tax or legal advice.",
    ctaLabel: "Set Up Rent Collection",
  },

  "property-inspection": {
    seoTitle: "NRI Property Inspection Hyderabad | Photo & Video Reports",
    seoDescription:
      "Property inspections for NRI owners in Hyderabad — periodic, move-in, move-out, damage and leakage inspections with photo/video documentation and recommended actions.",
    eyebrow: "Property Inspection",
    titleLines: ["Know What Is Happening at Your Property —", "Even From Thousands of Kilometres Away"],
    intro:
      "You shouldn't have to wonder. Our inspections give you documented, visual proof of your property's condition — with issues identified, actions recommended and costs estimated where possible.",
    blocks: [
      {
        heading: "Inspections we carry out",
        copy: "",
        items: [
          "Initial property inspection",
          "Periodic inspection",
          "Tenant move-in inspection",
          "Tenant move-out inspection",
          "Pre-rental inspection",
          "Post-vacancy inspection",
          "Damage inspection",
          "Leakage inspection",
          "Electrical inspection",
          "General condition inspection",
          "Photo documentation",
          "Video inspection",
          "Maintenance requirement identification",
        ],
      },
      {
        heading: "What every inspection report includes",
        copy: "",
        items: [
          "Date and property details",
          "Inspection status",
          "Photographs",
          "Videos where applicable",
          "Issues identified",
          "Recommended action",
          "Estimated cost where possible",
          "Owner approval status",
        ],
      },
    ],
    ctaLabel: "Request a Property Inspection",
  },

  "property-maintenance": {
    seoTitle: "NRI Property Maintenance Hyderabad | Repairs, Cleaning & Upkeep",
    seoDescription:
      "Complete property maintenance for NRI owners in Hyderabad — plumbing, electrical, painting, carpentry, AC, civil works, cleaning, pest control and security coordination.",
    eyebrow: "Complete Property Maintenance",
    titleLines: ["One Contact for Almost Everything", "Your Property Needs"],
    intro:
      "Stop coordinating ten different vendors from another country. Tell us what your property needs — we coordinate it, supervise it and show you the completed work.",
    blocks: [
      {
        heading: "Plumbing",
        items: ["Leakage", "Taps", "Flush systems", "Water lines", "Drainage", "Pumps", "Bathroom issues"],
      },
      {
        heading: "Electrical",
        items: ["Switches", "Sockets", "Lights", "Fans", "MCB issues", "Minor electrical repairs"],
      },
      {
        heading: "Painting",
        items: ["Full painting", "Touch-up painting", "Wall repair", "Repainting after tenant vacancy"],
      },
      {
        heading: "Carpentry & Woodwork",
        items: ["Doors", "Locks", "Hinges", "Cabinets", "Furniture", "Wardrobes", "Kitchen repairs", "Minor modular work"],
      },
      {
        heading: "AC & Appliances",
        items: ["AC servicing coordination", "Refrigerator", "Washing machine", "Geyser", "Kitchen appliances", "Other household equipment"],
      },
      {
        heading: "Civil Works",
        items: ["Tile repairs", "Wall repairs", "Minor masonry", "Waterproofing coordination", "Seepage investigation"],
      },
      {
        heading: "Cleaning",
        items: ["Regular cleaning", "Deep cleaning", "Move-out cleaning", "Move-in cleaning", "Bathroom cleaning", "Kitchen cleaning", "Balcony cleaning"],
      },
      {
        heading: "And more",
        items: ["Pest control", "Security & access coordination", "Interior works", "Vendor quotations", "Work supervision", "Completion photo/video proof"],
      },
    ],
    note: "Larger projects are coordinated through the group's professional execution network. No unnecessary work is carried out without owner approval, except genuine emergencies where immediate action prevents further damage.",
    ctaLabel: "Request Maintenance Support",
  },

  "property-make-ready": {
    seoTitle: "Property Make-Ready Services Hyderabad | Tenant Vacated? We Handle It",
    seoDescription:
      "Move-out to make-ready to re-rental in Hyderabad — inspection, condition report, deep cleaning, repairs, painting and tenant placement for NRI property owners.",
    eyebrow: "Move-Out → Make-Ready → New Tenant",
    titleLines: ["Tenant Vacated?", "We Take It From Here."],
    intro:
      "The gap between tenants is where remote ownership usually breaks down. We run the entire transition — from final inspection to a new tenant moving in — while you watch it happen through photos and reports.",
    process: {
      heading: "The 5-step transition",
      steps: [
        { title: "Inspection", copy: "We inspect the property after the tenant vacates." },
        { title: "Condition Report", copy: "Damages, missing items, cleaning requirements and maintenance issues documented with photos." },
        { title: "Make-Ready", copy: "Deep cleaning, pest control, plumbing, electrical, painting, carpentry, hardware replacement, appliance servicing, curtain/blind cleaning or replacement, minor civil repairs, furniture repairs and interior works — coordinated and supervised." },
        { title: "Final Presentation", copy: "The property is prepared and presented for the next tenant." },
        { title: "Re-Rental", copy: "Property photographs, listing preparation, tenant enquiries, screening, agreement coordination and move-in." },
      ],
    },
    blocks: [],
    ctaLabel: "Start My Make-Ready",
  },

  "move-in-move-out": {
    seoTitle: "Move-In & Move-Out Coordination Hyderabad | NRI Property Services",
    seoDescription:
      "Move-in and move-out coordination for NRI properties in Hyderabad — handovers, security deposits, condition documentation and final inspections.",
    eyebrow: "Move-In / Move-Out",
    titleLines: ["Every Handover,", "Documented"],
    intro:
      "Move-ins and move-outs are the moments where property condition, deposits and disputes are decided. We make sure both are handled properly — with evidence.",
    blocks: [
      {
        heading: "Move-in coordination",
        items: [
          "Property readiness check before move-in",
          "Tenant handover coordination",
          "Property condition documentation with photos/videos",
          "Security deposit coordination",
          "Agreement coordination support",
        ],
      },
      {
        heading: "Move-out coordination",
        items: [
          "Notice-period coordination",
          "Final inspection after vacating",
          "Damage and missing-item documentation",
          "Deposit settlement inputs based on condition",
          "Make-ready handover for the next tenant",
        ],
      },
    ],
    ctaLabel: "Coordinate a Handover",
  },

  "interior-services": {
    seoTitle: "Interior & Renovation Services Hyderabad | NRI Property Owners",
    seoDescription:
      "Interior and renovation coordination for NRI-owned properties in Hyderabad — design, modular furniture, painting, false ceiling, flooring and complete execution.",
    eyebrow: "Interior & Renovation Services",
    titleLines: ["Renovate From Abroad.", "Without Losing Control."],
    intro:
      "Upgrading a property you cannot visit takes more than a contractor — it takes supervision, documentation and accountability. Larger projects are coordinated through the group's professional execution network.",
    blocks: [
      {
        heading: "What we coordinate",
        items: [
          "Design coordination",
          "Renovation planning",
          "Modular furniture",
          "Painting and wall finishes",
          "Electrical and plumbing works",
          "False ceiling",
          "Flooring",
          "Complete interior execution",
        ],
      },
      {
        heading: "How you stay in control",
        copy: "Scope, quotations and timelines are shared before work begins. Progress is documented with photos and videos at every stage. Payments are milestone-based with your approval.",
        items: [],
      },
    ],
    ctaLabel: "Discuss My Renovation",
  },

  "painting-services": {
    seoTitle: "House Painting Services Hyderabad | NRI Property Painting & Touch-Up",
    seoDescription:
      "Painting coordination for NRI properties in Hyderabad — full painting, touch-ups, wall repair and repainting after tenant vacancy, with supervision and photo proof.",
    eyebrow: "Painting Services",
    titleLines: ["Fresh Paint.", "Zero Follow-Up."],
    intro:
      "Whether it's a full repaint after a tenant vacates or a touch-up before your family visits, we coordinate painters, supervise the work and show you the result before you release payment.",
    blocks: [
      {
        heading: "Painting work we coordinate",
        items: [
          "Full property painting",
          "Touch-up painting",
          "Wall repair and putty work",
          "Repainting after tenant vacancy",
          "Damp/seepage-affected wall treatment coordination",
          "Multiple vendor quotations on request",
        ],
      },
    ],
    ctaLabel: "Get a Painting Quote",
  },

  "plumbing-services": {
    seoTitle: "Plumbing Services Hyderabad | NRI Property Plumbing Repairs",
    seoDescription:
      "Plumbing coordination for NRI properties in Hyderabad — leakages, taps, flush systems, water lines, drainage, pumps and bathroom repairs with photo confirmation.",
    eyebrow: "Plumbing Services",
    titleLines: ["A Leak Shouldn't Cross", "Seven Seas to Reach You"],
    intro:
      "When your tenant reports a leak at midnight your time, it becomes our problem — not yours. We inspect, coordinate the plumber, supervise the fix and send you photo confirmation.",
    blocks: [
      {
        heading: "Plumbing work we coordinate",
        items: [
          "Leakage detection and repair",
          "Tap and faucet replacement",
          "Flush system repairs",
          "Water line issues",
          "Drainage and blockage",
          "Pump repairs",
          "Bathroom plumbing issues",
        ],
      },
    ],
    ctaLabel: "Fix a Plumbing Issue",
  },

  "electrical-services": {
    seoTitle: "Electrical Services Hyderabad | NRI Property Electrical Repairs",
    seoDescription:
      "Electrical repair coordination for NRI properties in Hyderabad — switches, sockets, lights, fans, MCB issues and minor electrical work, supervised and documented.",
    eyebrow: "Electrical Services",
    titleLines: ["Safe Hands for", "Every Switch and Socket"],
    intro:
      "Electrical issues need quick, competent handling — especially when you're not there to judge the work. We coordinate qualified electricians and verify the completed job.",
    blocks: [
      {
        heading: "Electrical work we coordinate",
        items: [
          "Switch and socket repairs",
          "Light and fan installation/repair",
          "MCB and fuse issues",
          "Minor electrical repairs",
          "Electrical safety inspection",
          "Appliance point installation",
        ],
      },
    ],
    ctaLabel: "Fix an Electrical Issue",
  },

  "rental-agreement-assistance": {
    seoTitle: "Rental Agreement Assistance Hyderabad | Documentation & Compliance",
    seoDescription:
      "Rental agreement, tenant verification, police verification and property documentation assistance for NRI owners in Hyderabad — coordinated with the right service providers.",
    eyebrow: "Documentation & Compliance Assistance",
    titleLines: ["The Paperwork,", "Handled Properly"],
    intro:
      "Agreements, verifications, bills, society letters — property paperwork multiplies when you're abroad. We coordinate it, digitise it and keep it retrievable.",
    blocks: [
      {
        heading: "What we coordinate",
        items: [
          "Rental agreement coordination",
          "Agreement renewal",
          "Tenant verification assistance",
          "Police verification assistance",
          "Property-related document coordination",
          "Utility bill coordination",
          "Society/RWA communication",
          "Maintenance bill coordination",
          "Property tax coordination",
          "Document collection",
          "Digital document storage",
        ],
      },
      {
        heading: "A clear boundary",
        copy: "Legal, tax and regulatory matters are handled through your appointed professionals or appropriate service providers where required. We coordinate and facilitate — we do not act as a law firm or tax consultancy.",
        items: [],
      },
    ],
    ctaLabel: "Get Documentation Help",
  },
};
